import { FC } from "react";
import { Flex, FlexProps } from "shared/components/Spor";
import { VariantColor, variantToColor } from "shared/utils/colorUtils";
import { useTheme } from "styled-components";

type DashedLineProps = Omit<FlexProps, "backgroundColor" | "background"> & {
  variant?: VariantColor;
  /** When set, overrides variant and uses this color for the line (e.g. from a ColorScheme). */
  color?: string;
  invert?: boolean;
  width?: string;
  height?: string;
  strokeWidth?: string;
  strokeDasharray?: string;
  orientation?: "horizontal" | "vertical";
};

/**
 * Place the <DashedLine/> instead of gaps, such as:
 * ╰---╯
 *  - - <- Here
 * ╭---╮
 * The dashed line should therefore have the same color as the container boxes.
 * If you do not have container boxes, then use <DashedLineStandalone/>
 *
 * Tips: Use <SegmentedPanel/> to automatically style childs in container boxes
 * and add <DashedLine/> between each children.
 */
const DashedLine: FC<DashedLineProps> = ({
  variant = "main",
  color: colorProp,
  invert,
  width,
  height,
  strokeWidth,
  strokeDasharray,
  orientation = "horizontal", // default keeps old behavior
  ...props
}) => {
  // Each mask id must be unique, since they for some reason can affect one another
  // eslint-disable-next-line react-hooks/purity -- Time based pseudorandomness, sue me
  const uuid = Math.random();

  const theme = useTheme();
  const color =
    colorProp !== undefined ? colorProp : variantToColor(variant, theme);

  const isVertical = orientation === "vertical";

  const defaultConfig = isVertical
    ? { outerWidth: "3px", outerHeight: "100%" }
    : { outerWidth: "80%", outerHeight: "3px" };

  const outerWidth = width ?? defaultConfig.outerWidth;
  const outerHeight = height ?? defaultConfig.outerHeight;
  const svgHeight = isVertical ? outerHeight : "3px";

  return (
    <Flex
      className="dashedLine" // Used in SegmentedPanel.tsx
      justifyContent="center"
      {...props}
    >
      <svg
        width={outerWidth}
        height={svgHeight}
        xmlns="http://www.w3.org/2000/svg"
      >
        <mask id={`inverseMask_${uuid}`}>
          <rect
            width="100%"
            height={isVertical ? "100%" : "4px"}
            fill={invert ? "black" : "white"}
          />
          {isVertical ? (
            <line
              x1="50%"
              y1="0"
              x2="50%"
              y2="100%"
              stroke={invert ? "white" : "black"}
              strokeWidth={strokeWidth ?? "4"}
              strokeLinecap="round"
              strokeDasharray={strokeDasharray ?? "3 9"}
            />
          ) : (
            <line
              x1="0"
              y1="1.5"
              x2="100%"
              y2="1.5"
              stroke={invert ? "white" : "black"}
              strokeWidth={strokeWidth ?? "4"}
              strokeLinecap="round"
              strokeDasharray={strokeDasharray ?? "3 9"}
            />
          )}
        </mask>
        <rect
          width="100%"
          height={isVertical ? "100%" : "4px"}
          fill={color}
          mask={`url(#inverseMask_${uuid})`}
        />
      </svg>
    </Flex>
  );
};
export default DashedLine;
